import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useIntl} from "react-intl";
import {notify} from "components/VoiceChat/Components/Notify";
import {OneBtnPopup, showPopUp} from "components/PopUp";
import AgoraRTM from "agora-rtm-sdk";

const CHANNEL_NAME = "redbrickLand";
const APP_ID = "ae1d008bd852448ebc15c6ad02b4ebd9";
const client = AgoraRTM.createInstance(APP_ID);
const channel = client.createChannel(CHANNEL_NAME);
let sendId = 0;
let _disturbMode = false;

export default function useAgoraRTM() {
  const intl = useIntl();
  const userInfo = useSelector(state => state.userinfo);
  const roomName = useSelector(state => state.chat.currentRoom);
  const disturbMode = useSelector(state => state.voiceChat.disturbMode);
  const [status, setStatus] = useState("onLine"); //onLine, offLine, calling, meeting, cancel
  const [privateChatInfo, setPrivateChatInfo] = useState(null);
  const [privateOtherPersonName, setPrivateOtherPersonName] = useState(null);
  const [callId, setCallId] = useState(null);

  useEffect(() => {
    _disturbMode = disturbMode;
  }, [disturbMode]);

  useEffect(() => {
    channel.on("MemberLeft", function (memberId) {
      if (memberId === callId) {
        setStatus("cancel");
      }
    });
  }, [channel]);

  async function login(token) {
    const _id = String(userInfo.id);
    const uid = new Buffer.from(_id).toString("base64");
    const rtmOption = {uid, token};
    await client.login(rtmOption);

    const {name, icon} = userInfo;

    await client.addOrUpdateLocalUserAttributes({name, icon, status, roomName});

    await channel.join();

    peerInvitation();
  }

  function logout() {
    client.logout();
  }

  async function getUserAttributes(id) {
    return await client.getUserAttributes(id);
  }

  const peerInvitation = () => {
    client.on("RemoteInvitationReceived", async remoteInvitation => {
      const isValidUser = userInfo.verifiedEmail && userInfo.phone;
      if (!isValidUser || _disturbMode) {
        return refuseCall(remoteInvitation);
      }

      peerEvents(remoteInvitation);

      try {
        const {name} = await getUserAttributes(remoteInvitation.callerId);
        const button = await notify(
          "receive",
          "ID_VOICE_CHAT_FLOATING_CHATTING_REQUEST",
          name,
        );
        if (button === "refuse") {
          refuseCall(remoteInvitation);
        } else if (button === "accept") {
          acceptCall(remoteInvitation);
          setPrivateOtherPersonName(name);
          channel.on("MemberLeft", function (memberId) {
            if (memberId === remoteInvitation.callerId) {
              setStatus("cancel");
            }
          });
        }
      } catch (e) {
        refuseCall(remoteInvitation);
      }
    });
  };

  const peerEvents = remoteInvitation => {
    remoteInvitation.on("RemoteInvitationCanceled", () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      notify("default", "ID_VOICE_CHAT_PRIVATE_CHAT_END");
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
    });

    remoteInvitation.on("RemoteInvitationAccepted", async () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      setStatus("meeting");
      client.addOrUpdateLocalUserAttributes({status});
    });

    remoteInvitation.on("RemoteInvitationRefused", () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
    });

    remoteInvitation.on("RemoteInvitationFailure", () => {
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
    });
  };

  const setRefusedList = id => {
    let voiceChatRefusedList = localStorage.getItem("voiceChatRefusedList");

    if (!voiceChatRefusedList) {
      voiceChatRefusedList = {[id]: new Date()};
    } else {
      voiceChatRefusedList = JSON.parse(voiceChatRefusedList);
      voiceChatRefusedList[id] = new Date();
    }

    localStorage.setItem(
      "voiceChatRefusedList",
      JSON.stringify(voiceChatRefusedList),
    );
  };

  const getRefusedList = id => {
    const voiceChatRefusedList = localStorage.getItem("voiceChatRefusedList");
    const _voiceChatRefusedList = JSON.parse(voiceChatRefusedList);

    if (!_voiceChatRefusedList) return false;
    else {
      if (_voiceChatRefusedList[id]) {
        const refusedTime = new Date(_voiceChatRefusedList[id]);
        const nowTime = new Date();
        const seconds = (nowTime.getTime() - refusedTime.getTime()) / 1000;
        if (seconds < 180) {
          return seconds;
        } else {
          delete _voiceChatRefusedList[id];
          localStorage.setItem(
            "voiceChatRefusedList",
            JSON.stringify(_voiceChatRefusedList),
          );
          return false;
        }
      }
      return false;
    }
  };

  const sendCall = async id => {
    const localInvitation = client.createLocalInvitation(id.toString());
    const {name, status} = await getUserAttributes(localInvitation.calleeId);

    if (status === "calling" || status === "meeting") {
      return showPopUp(
        <OneBtnPopup
          title={intl.formatMessage(
            {
              id: "ID_VOICE_CHAT_FLOATING_CHATTING_DIFFERENT",
            },
            {
              user: name,
            },
          )}
        />,
      );
    }

    const isRefusedIn3min = getRefusedList(id);
    if (isRefusedIn3min) {
      const min = Math.trunc(3 - isRefusedIn3min / 60);
      const sec = Math.trunc(60 - (isRefusedIn3min % 60));
      return showPopUp(
        <OneBtnPopup
          title={intl.formatMessage(
            {id: "ID_VOICE_CHAT_FLOATING_CHATTING_REAPPLY"},
            {user: name, min, sec},
          )}
        />,
        {defaultPadding: false},
      );
    }

    const base64UserId = new Buffer.from(userInfo.id).toString("base64");
    const callerId = base64UserId.substring(0, 5);
    const calleeId = localInvitation.calleeId.substring(0, 5);
    setPrivateChatInfo(`caller${callerId}callee${calleeId}`);
    setPrivateOtherPersonName(name);

    localInvitation.on("LocalInvitationCanceled", () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      notify("default", "ID_VOICE_CHAT_PRIVATE_CHAT_END");
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
    });

    localInvitation.on("LocalInvitationAccepted", async () => {
      setCallId(localInvitation.calleeId);
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      notify("default", "ID_VOICE_CHAT_FLOATING_CHATTING_ACCEPT", name);
      setStatus("meeting");
      client.addOrUpdateLocalUserAttributes({status});

      channel.on("MemberLeft", function (memberId) {
        if (memberId === localInvitation.calleeId) {
          setStatus("cancel");
        }
      });
    });

    localInvitation.on("LocalInvitationRefused", async () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }

      notify("default", "ID_VOICE_CHAT_FLOATING_CHATTING_DENY", name);
      setRefusedList(id);
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
    });

    localInvitation.on("LocalInvitationFailure", () => {
      if (sendId) {
        clearTimeout(sendId);
        sendId = 0;
      }
      setStatus("cancel");
      client.addOrUpdateLocalUserAttributes({status});
    });

    localInvitation.send();
    setStatus("calling");
    client.addOrUpdateLocalUserAttributes({status});
    sendId = setTimeout(
      invitation => {
        sendId = 0;
        cancelCall(invitation);
        notify("default", "ID_VOICE_CHAT_FLOATING_CHATTING_DENY", name);
        setRefusedList(id);
      },
      60000,
      localInvitation,
    );

    try {
      await notify("send", "ID_VOICE_CHAT_FLOATING_CHATTING_APPLY", name);
      cancelCall(localInvitation);
    } catch (e) {
      cancelCall(localInvitation);
    }
  };

  const cancelCall = localInvitation => {
    localInvitation.cancel();
    client.addOrUpdateLocalUserAttributes({status});
    setStatus("cancel");
  };

  const acceptCall = remoteInvitation => {
    remoteInvitation.accept();
    const callerId = remoteInvitation.callerId.substring(0, 5);
    const base64UserId = new Buffer.from(userInfo.id).toString("base64");
    const calleeId = base64UserId.substring(0, 5);
    setPrivateChatInfo(`caller${callerId}callee${calleeId}`);
    setCallId(remoteInvitation.callerId);
    setStatus("meeting");
    client.addOrUpdateLocalUserAttributes({status});
  };

  const refuseCall = remoteInvitation => {
    remoteInvitation.refuse();
    setStatus("onLine");
    client.addOrUpdateLocalUserAttributes({status});
  };

  const getMembers = () => {
    return channel.getMembers();
  };

  return {
    callId,
    privateChatInfo,
    status,
    privateOtherPersonName,
    login,
    logout,
    getUserAttributes,
    sendCall,
    getMembers,
    setStatus,
  };
}
